// PRD §7.5.5 — mission detail + the explicit-consequence interest modal.
// The consequenceCopy comes from the server (locked template per mission.status);
// we render verbatim. Never substitute a tone "improvement" client-side.
import { useEffect, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { Loader2, ArrowLeft, AlertTriangle, Check } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

export default function MissionDetail() {
  const [, params] = useRoute("/mentor/missions/:id");
  const [, setLocation] = useLocation();
  const [data, setData] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!params?.id) return;
    const tok = localStorage.getItem("mentor_token") || "";
    fetch(`${BASE}api/mentor-portal/missions/${params.id}`, { headers: { "x-mentor-token": tok } })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.error || `${r.status}`)))
      .then(setData).catch(e => setErr(String(e)));
  }, [params?.id]);

  async function express() {
    if (busy) return;
    setBusy(true);
    const tok = localStorage.getItem("mentor_token") || "";
    try {
      const r = await fetch(`${BASE}api/mentor-portal/missions/${params!.id}/express-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-mentor-token": tok },
        body: JSON.stringify({ confirmed: true }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Failed"); setShowModal(false); return; }
      setDone(true); setShowModal(false);
    } finally { setBusy(false); }
  }

  if (err) return <div className="max-w-3xl mx-auto px-6 py-10 text-sm text-red-400">{err}</div>;
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-10"><ProgressLoader active={true} title="Loading" stages={["Loading mission", "Checking your prior interest"]} /></div>;

  const m = data.mission;
  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <button onClick={() => setLocation("/mentor")} className="text-xs text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)] inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to browse
      </button>

      <div className="text-xs text-[var(--mentor-fg-muted)] mb-1">{data.company?.name}</div>
      <h1 className="text-2xl font-bold mb-3">{m.title}</h1>

      {m.authorKind === "viren_draft" && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-md p-3 mb-4 text-xs text-amber-200">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>This mission was drafted by Viren (our AI) from the founder's recent check-ins. The founder has not yet seen it. Your interest will be forwarded along with the draft.</div>
        </div>
      )}

      <Section title="Problem">{m.problemStatement}</Section>
      <Section title="Deliverable">{m.deliverable}</Section>
      <Section title="What good looks like">{m.whatGoodLooksLike}</Section>

      {m.domainTags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-4">
          {m.domainTags.map((t: string) => <span key={t} className="text-[10px] bg-[var(--mentor-tag-bg)] text-[var(--mentor-fg-muted)] px-1.5 py-0.5 rounded">{t}</span>)}
        </div>
      )}

      {done ? (
        <div className="mt-8 bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4 text-sm text-emerald-200 flex items-center gap-2">
          <Check className="w-4 h-4" /> Interest expressed. {data.founderName} has {data.slaDays} days to respond. We'll email you either way.
        </div>
      ) : (
        <button onClick={() => setShowModal(true)}
          className="mt-8 w-full bg-[#FF5C00] hover:bg-[#FF5C00]/90 text-white text-sm font-semibold rounded-md py-2.5"
          data-testid="button-mentor-express-interest-open">
          I am interested in this mission
        </button>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4">
          <div className="bg-[var(--mentor-card)] border border-[var(--mentor-border)] rounded-lg p-6 max-w-lg w-full">
            <h3 className="text-base font-bold mb-3">Confirm your interest</h3>
            <div className="text-sm text-[var(--mentor-fg)] leading-relaxed mb-5 whitespace-pre-wrap" data-testid="text-mentor-consequence-copy">
              {data.consequenceCopy}
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setShowModal(false)} disabled={busy} className="text-sm text-[var(--mentor-fg-muted)] hover:text-[var(--mentor-fg)] px-3 py-2" data-testid="button-mentor-express-interest-cancel">Cancel</button>
              <button onClick={express} disabled={busy} className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-50 text-white text-sm font-semibold rounded-md px-4 py-2 flex items-center gap-1.5" data-testid="button-mentor-express-interest-confirm">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {busy ? "Sending" : "I confirm — express interest"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--mentor-fg-muted)] mb-1.5">{title}</div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed">{children}</div>
    </div>
  );
}
